// Projection helpers. cm-accurate field math must be done in projected METERS, never in raw lat/lon
// degrees (1e-7° ≈ 1.1 cm and 1° lon ≠ 1° lat). We project to ETRS89/UTM (WGS84 datum is fine for
// RELATIVE distances within a field — the same transform is applied to lane and vehicle, so any
// datum offset cancels). Bayern spans UTM zones 32N (lon < 12°E) and 33N (lon ≥ 12°E).

import proj4 from 'proj4';
import type { LonLat } from './types';

const WGS84 = 'EPSG:4326';

/** A point in projected meters (easting, northing). */
export type MeterPoint = [number, number];

export interface Projector {
  /** EPSG code of the projected CRS (32632 or 32633). */
  readonly epsg: number;
  /** WGS84 [lon, lat] → projected meters [E, N]. */
  toMeters(p: LonLat): MeterPoint;
  /** Projected meters [E, N] → WGS84 [lon, lat]. */
  toLonLat(p: MeterPoint): LonLat;
}

/** UTM zone (32 or 33) for a given longitude, per the Bayern split at 12°E. */
export function utmZoneForLon(lon: number): 32 | 33 {
  return lon < 12 ? 32 : 33;
}

/** EPSG code for the UTM zone covering a longitude. */
export function utmEpsgForLon(lon: number): 32632 | 32633 {
  return utmZoneForLon(lon) === 32 ? 32632 : 32633;
}

function utmDef(zone: 32 | 33): string {
  return `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs +type=crs`;
}

/**
 * Build a Projector for the UTM zone covering `referenceLon`. Pass the field/course centroid
 * longitude so the whole field uses one consistent metric CRS.
 */
export function projectorForLon(referenceLon: number): Projector {
  const zone = utmZoneForLon(referenceLon);
  const def = utmDef(zone);
  return {
    epsg: utmEpsgForLon(referenceLon),
    toMeters: (p) => proj4(WGS84, def, p) as MeterPoint,
    toLonLat: (p) => proj4(def, WGS84, p) as LonLat,
  };
}

/** Mean longitude of a list of points — a stable reference for choosing the UTM zone. */
export function meanLon(points: readonly LonLat[]): number {
  if (points.length === 0) return 0;
  let sum = 0;
  for (const [lon] of points) sum += lon;
  return sum / points.length;
}
